import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ResponsiveImage from './ResponsiveImage';

// This component optimizes video loading on mobile by:
// 1. Initially showing a static poster image
// 2. Only loading the video when the user has scrolled to it or after a delay
// 3. Using the poster attribute for better initial load performance

const OptimizedHomeVideo = ({ isMobile, videoSrc, mobileSrc }) => {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const containerRef = useRef(null);
  
  // Use the correct video source based on device
  const currentVideoSrc = isMobile ? mobileSrc : videoSrc;
  
  // Use WebP poster images for better performance
  const desktopPosterPath = '/assets/images/posters/video-poster.webp';
  const mobilePosterPath = '/assets/images/posters/mobile-video-poster.webp';
  const posterSrc = isMobile ? mobilePosterPath : desktopPosterPath;
  
  // Truly lazy load video - only on user interaction or when in viewport
  useEffect(() => {
    // Use Intersection Observer to load video when it's about to be visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoadVideo(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '50px' } // Start loading 50px before visible
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    // Also load on user interaction (click, touch, scroll)
    const handleUserInteraction = () => {
      setShouldLoadVideo(true);
      observer.disconnect();
    };
    
    window.addEventListener('click', handleUserInteraction, { once: true });
    window.addEventListener('touchstart', handleUserInteraction, { once: true });
    window.addEventListener('scroll', handleUserInteraction, { once: true, passive: true });
    
    return () => {
      observer.disconnect();
      window.removeEventListener('click', handleUserInteraction);
      window.removeEventListener('touchstart', handleUserInteraction);
      window.removeEventListener('scroll', handleUserInteraction);
    };
  }, []);
  
  // Handle video loaded event
  const handleVideoLoaded = () => {
    setIsVideoLoaded(true);
  };
  
  return (
    <div ref={containerRef} className={`video-container ${isVideoLoaded ? 'video-loaded' : ''}`}>
      {/* Always show the poster image initially */}
      <ResponsiveImage 
        src={posterSrc}
        alt="Mevedir Himalayan Organic Products"
        className="video-poster-image"
        style={{ 
          opacity: isVideoLoaded ? 0 : 1,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1,
          transition: 'opacity 1.2s ease-in-out' // Longer, smoother transition
        }}
        loading="eager" // Load this immediately as it's above the fold
        srcSet={[
          { src: '/assets/images/posters/mobile-video-poster.webp', width: 640 },
          { src: '/assets/images/posters/video-poster.webp', width: 1280 }
        ]}
        sizes="100vw"
        decoding="async"
      />
      
      {/* Only render video element when we're ready to load it */}
      {shouldLoadVideo && (
        <video 
          autoPlay 
          muted 
          loop 
          className="main-video"
          poster={posterSrc}
          preload="none"
          onLoadedData={handleVideoLoaded}
          style={{ 
            opacity: isVideoLoaded ? 1 : 0,
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 2, // Higher z-index to ensure visibility
            transition: 'opacity 1.2s ease-in-out' // Longer, smoother transition
          }}
          playsInline // Better mobile support
        >
          <source src={currentVideoSrc} type="video/webm" />
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
};

OptimizedHomeVideo.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  videoSrc: PropTypes.string.isRequired,
  mobileSrc: PropTypes.string.isRequired
};

export default OptimizedHomeVideo;